import React, { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import ContentBodyWrapper from 'components/custom/ContentBodyWrapper'
import PaperWrapper from 'components/custom/PaperWrapper'
import { componentPermissions } from 'config/permissions'
import { componentNames } from 'config/roles'
import { interpreterRequestSelector } from 'store/slices/interpreterRequestSlice'
import { interpreterResultSelector } from 'store/slices/interpreterResultSlice'
import { sagaInterpretBomInputAction } from 'saga/actions/interpreter'

import { BOMProcessor } from './components/bomProcessor'
import { BOMProcessedTable } from './components/bomProcessedTable'
import { BOMInterpretedTable } from './components/bomIntepretedTable'
import { RoutePaths } from 'routes/paths'
import { resetInterpreterResult } from 'store/slices/interpreterResultSlice'
import { resetInterpreterRequest } from 'store/slices/interpreterRequestSlice'
import ButtonWrapper from 'components/custom/ButtonWrapper'
import { eColor, eTypographyVariant } from 'components/types'
import { SustamizeSpinner } from './components/sustamizeSpinner/sustamizeSpinner'
import TitleWrapper from 'components/custom/TitleWrapper'
import TextWrapper from 'components/custom/TextWrapper'

const statusMessages = [
  'Picking up reading glasses...',
  'What do we have here...',
  'Alright, contextualizing input...',
  'Matching entities...',
  'Oops, this needs a change...',
  'Retrieving respective emission factors...',
  'Formating the results...',
  'Final polishing...',
]

const Interpreter: FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const bomRequest = useSelector(interpreterRequestSelector)
  const bomResult = useSelector(interpreterResultSelector)

  const [loading, setLoading] = useState(false)
  const [statusIndex, setStatusIndex] = useState(0)

  const handleProcessBOM = () => {
    if (!bomRequest?.bomData?.length) return
    setLoading(true)
    dispatch(sagaInterpretBomInputAction(bomRequest.bomData))
  }

  const handleReset = () => {
    dispatch(resetInterpreterResult())
    dispatch(resetInterpreterRequest())
    setLoading(false)
  }

  useEffect(() => {
    if (bomResult?.data) {
      setLoading(false)
    }
  }, [bomResult])

  useEffect(() => {
    if (!loading) return

    const interval = setInterval(() => {
      setStatusIndex((prev) => {
        if (prev < statusMessages.length - 1) {
          return prev + 1
        } else {
          return prev
        }
      })
    }, 8000)

    return () => clearInterval(interval)
  }, [loading])

  return (
    <ContentBodyWrapper title={t('toolbox.interpreter')}>
      <PaperWrapper>
        <TitleWrapper
          variant={eTypographyVariant.h1}
          className="text-2xl font-bold mb-4"
        >
          BOM Interpreter
        </TitleWrapper>
        <TextWrapper className="text-base d-flex">
          Paste or upload your BOM, and we'll match materials, classify them,
          and calculate reference emissions.
        </TextWrapper>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <SustamizeSpinner />
            <TextWrapper className="text-sm text-gray-600 mt-2">
              {statusMessages[statusIndex]}
            </TextWrapper>
          </div>
        ) : bomResult ? (
          <>
            <BOMInterpretedTable data={bomResult.data} />
            <div className="mt-4 text-right">
              <ButtonWrapper onClick={handleReset} color={eColor.secondary}>
                Reset
              </ButtonWrapper>
            </div>
          </>
        ) : (
          <>
            <BOMProcessor />
            {bomRequest?.bomData && (
              <>
                <BOMProcessedTable data={bomRequest.bomData} />
                <ButtonWrapper
                  disabled={loading}
                  onClick={handleProcessBOM}
                  color={eColor.primary}
                >
                  {loading ? 'Processing...' : 'Process BOM'}
                </ButtonWrapper>
              </>
            )}
          </>
        )}
      </PaperWrapper>
    </ContentBodyWrapper>
  )
}

const exportObj = {
  routeProps: {
    path: RoutePaths.toolboxInterpreter,
    title: 'toolbox.interpreter',
    element: <Interpreter />,
    roles: componentPermissions[componentNames.pageInterpreter],
    requiresAuthentication: true,
  },
  name: 'Interpreter',
}

export default exportObj
